import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
  CALCULATOR_CATEGORIES,
  CALCULATOR_TOOLS,
} from "../data/calculatorCatalog";
import {
  MATERIAL_DATABASE,
  MATERIAL_GROUPS,
  TOOL_COATINGS,
  TOOL_MATERIAL_OPTIONS,
  HARDNESS_DATA_POLICY,
  getRockwellHardness,
} from "../data/machiningMaterials";
import { STANDARDS_AUDIT } from "../data/standardsAudit";
import {
  FORMULA_CATEGORIES,
  FORMULA_REFERENCE_DATABASE,
} from "../data/formulaReferenceDatabase";
import "./ReferencePage.css";

const formulaSections = [
  {
    title: "Machining",
    accent: "cyan",
    formulas: [
      ["Spindle speed", "RPM = (SFM x 3.82) / cutter diameter", "Imperial milling, drilling, and turning speed conversion."],
      ["Cutting speed", "SFM = (RPM x diameter) / 3.82", "Use the tool or stock diameter that actually defines surface speed."],
      ["Feedrate", "IPM = RPM x flutes x chip load", "For milling. For drilling, use RPM x feed per revolution."],
      ["Material removal rate", "MRR = width of cut x depth of cut x feedrate", "Imperial rectangular milling estimate in cubic inches per minute."],
      ["Cutting horsepower", "HP = MRR x unit horsepower", "Estimate only; hardness, geometry, coolant, and machine condition matter."],
      ["Drill tip allowance", "Allowance = (drill diameter / 2) / tan(point angle / 2)", "Adds point depth so the full drill diameter reaches target depth."],
    ],
  },
  {
    title: "Sheet Metal & Fabrication",
    accent: "yellow",
    formulas: [
      ["Bend allowance", "BA = angle radians x (inside radius + K x thickness)", "Standard developed-length shop math for press brake layout."],
      ["K-factor", "K = ((BA / angle radians) - inside radius) / thickness", "Measured reverse calculation; typical values often fall between 0 and 0.5."],
      ["Kerf offset", "Offset = kerf width / 2", "Outside profiles offset outward; inside holes offset inward."],
      ["Weight from volume", "Weight = volume x density x quantity", "Use shape-specific volume and material density."],
    ],
  },
  {
    title: "Inspection & GD&T",
    accent: "green",
    formulas: [
      ["True position", "TP = 2 x sqrt(X deviation^2 + Y deviation^2)", "GD&T true position is commonly reported as a diametrical zone."],
      ["Worst-case stack", "Total tolerance = sum of absolute contributing tolerances", "Conservative assembly tolerance method."],
      ["RSS stack", "RSS = sqrt(tol1^2 + tol2^2 + ...)", "Statistical estimate when contributors are independent and centered."],
      ["Cp", "Cp = (USL - LSL) / (6 x sigma)", "Process spread compared with specification width."],
      ["Cpk", "Cpk = min((USL - mean), (mean - LSL)) / (3 x sigma)", "Capability with centering included."],
    ],
  },
  {
    title: "Welding, Additive & Business",
    accent: "purple",
    formulas: [
      ["Weld heat input", "kJ/unit = (volts x amps x 60 x efficiency) / (travel speed x 1000)", "Travel speed unit determines whether output is kJ/in or kJ/mm."],
      ["Print material cost", "Cost = grams used x spool cost / spool grams", "Use slicer material usage for the best estimate."],
      ["Failure-adjusted print cost", "Adjusted cost = base cost / (1 - failure rate)", "Failure rate must be entered as a decimal percentage."],
      ["Job quote", "Quote = break-even cost / (1 - profit margin)", "Margin-style quote math. Markup-style math is a different convention."],
      ["OEE", "OEE = availability x performance x quality", "Used to estimate productive equipment output."],
      ["3S audit score", "Overall 3S = average(Sort, Set in Order, Shine)", "Lean workplace organization score based on observable audit criteria."],
      ["Red-tag recovery", "Lost hours = people x minutes lost x shifts / 60", "Estimates visible waste from searching, walking, waiting, and clutter."],
    ],
  },
];

const resources = [
  {
    title: "Calculator Workflow",
    accent: "purple",
    items: [
      "Start with the manufacturing process and unit system before entering dimensions.",
      "Use material and tool presets as starting points, then adjust for your cutter, insert grade, coolant, setup rigidity, and machine condition.",
      "Save finished calculations to a manufacturing plan so they stay tied to the customer, job, and part.",
    ],
  },
  {
    title: "3S Lean Workplace Organization",
    accent: "green",
    items: [
      "Sort removes tools, material, documents, fixtures, and supplies that are not needed for the current work.",
      "Set in Order assigns visible, labeled, point-of-use locations so needed items are easy to find, use, return, and audit.",
      "Shine means clean while inspecting, so leaks, broken tools, missing guards, damaged cords, chip buildup, and abnormal conditions become visible.",
    ],
  },
  {
    title: "Shop Verification Checklist",
    accent: "cyan",
    items: [
      "Verify all critical calculator outputs against the print, customer specification, and company standard before cutting.",
      "Confirm machine travel, workholding clearance, tool stickout, spindle direction, coolant, and safe Z heights.",
      "For G-code, always dry run, single block, graphics verify, and prove out above the part before production.",
    ],
  },
  {
    title: "Business Account Setup",
    accent: "green",
    items: [
      "Owners can use the admin dashboard to manage employees, invite links, seats, projects, and company-level work.",
      "Employee invite accounts are treated as company seats and should not be redirected to billing.",
      "Use manufacturing plans and SOP Builder to keep calculations, process steps, cost assumptions, and job notes together.",
    ],
  },
  {
    title: "Accuracy Notes",
    accent: "orange",
    items: [
      "Many results are estimates because tooling, material condition, heat treat, machine condition, and shop standards vary.",
      "Press brake tonnage, weld heat input, shrinkage, feeds/speeds, and quoting should be reviewed against real equipment data.",
      "When a calculator uses a preset, treat it as a starting point unless your supplier, machine builder, tooling vendor, or quality system says otherwise.",
    ],
  },
  {
    title: "Standards Policy",
    accent: "purple",
    items: [
      "Reference formulas should be traceable to accepted shop math, inspection practice, ASME/ISO-style standards, or manufacturer data types.",
      "Estimated outputs must be clearly labeled as estimates and must show the practical variables that can change the result.",
      "Threading, GD&T, pressure work, welding procedure work, press brake loading, and live G-code require gage/code/machine verification before production.",
    ],
  },
];

const resourceCards = [
  {
    title: "Machining Setup Checklist",
    category: "Machining",
    description: "A practical pre-cut review for datum, workholding, tool stickout, coolant, safe Z, speed/feed basis, and first-piece inspection.",
    tags: ["setup", "milling", "turning", "safety"],
    link: "/dashboard?category=machining",
  },
  {
    title: "Thread Reference Workflow",
    category: "Threads",
    description: "Use tap drill, thread pitch, pipe thread, percent thread, thread engagement, and thread cutting depth tools together for common threading work.",
    tags: ["NPT", "UNC", "UNF", "metric", "tap drill"],
    link: "/dashboard?category=threads",
  },
  {
    title: "Drill, Counterbore, and Countersink Planning",
    category: "Holemaking",
    description: "Plan full-diameter depth, screw clearance, counterbore depth, countersink depth, spot drilling, reaming allowance, and annular cutter speed.",
    tags: ["drilling", "counterbore", "countersink", "reaming"],
    link: "/dashboard?category=holemaking",
  },
  {
    title: "GD&T and Inspection Tools",
    category: "Inspection",
    description: "Reference GD&T symbols and calculate true position, MMC bonus tolerance, go/no-go limits, tolerance stacks, sine bar setups, and process capability.",
    tags: ["GD&T", "true position", "MMC", "Cpk"],
    link: "/dashboard?category=inspection",
  },
  {
    title: "Welding Estimate Reference",
    category: "Welding",
    description: "Use weld heat input, filler estimate, weld volume, travel speed, gas flow, throat, and weld cost tools with process-specific verification.",
    tags: ["heat input", "filler", "GMAW", "GTAW", "SMAW"],
    link: "/dashboard?category=welding",
  },
  {
    title: "Sheet Metal Bend Reference",
    category: "Sheet Metal",
    description: "Connect bend allowance, bend deduction, flat pattern length, K-factor, press brake tonnage, kerf, and sheet yield calculations.",
    tags: ["bend allowance", "K-factor", "tonnage", "flat pattern"],
    link: "/dashboard?category=sheet-metal",
  },
  {
    title: "Shared Material Data",
    category: "Materials",
    description: "Review shared material presets, Rockwell hardness reference values, tool materials, coatings, density, and machinability baselines.",
    tags: ["Hardox", "steel", "aluminum", "density", "hardness"],
    link: "/material-data",
  },
  {
    title: "CNC and G-Code Verification",
    category: "CNC / G-Code",
    description: "Use starter code, arc center, work offset shift, safe-start blocks, kerf compensation, and bolt pattern output with dry-run verification.",
    tags: ["G-code", "G54", "IJK", "dry run"],
    link: "/dashboard?category=cnc-gcode",
  },
  {
    title: "Shop Quoting Workflow",
    category: "Costing",
    description: "Combine material cost, machine time, tooling, batch pricing, markup/margin, print cost, weld cost, and quote builder outputs.",
    tags: ["quote", "cost", "margin", "batch"],
    link: "/dashboard?category=costing",
  },
  {
    title: "3S Workspace Organization",
    category: "3S",
    description: "Sort unused fields, set pinned/recent tools in order, and shine the workspace with a practical health check.",
    tags: ["Sort", "Set in Order", "Shine", "lean"],
    link: "/3s",
  },
  {
    title: "Formula Reference Database",
    category: "Formulas",
    description: "Search the formulas used across ManuCalc, including variables, examples, units, assumptions, and related calculators.",
    tags: ["formula", "standards", "shop math"],
    link: "/formula-reference",
  },
  {
    title: "Safety and Verification Notes",
    category: "Safety",
    description: "Always verify calculator outputs against the print, customer spec, company standard, machine/tooling limits, and first-piece inspection.",
    tags: ["safety", "verification", "inspection", "quality"],
    link: "/formula-reference",
  },
];

const whatsNew = [
  {
    title: "Manufacturing Copilot Workspace",
    date: "June 2026",
    text: "Rebuilt the AI Assistant as a full-screen manufacturing copilot with multiple attached ManuCalc records, manufacturing-specific modes, professional response formatting, a right-side saved-chat library, chat search and rename, and mobile drawers.",
  },
  {
    title: "AI Manufacturing Assistant",
    date: "June 2026",
    text: "Added an authenticated, chat-style manufacturing assistant with process planning, tooling, quote, troubleshooting, setup-sheet, and lean-improvement modes. Conversations can be saved independently or attached to Manufacturing Plans.",
  },
  {
    title: "Manufacturing Shop OS",
    date: "June 2026",
    text: "Added Tool Library, Fixture Library, Machine Database, Setup Sheets, Job Travelers, Inspection Sheets, Shop Brain, Shop Floor Mode, QR access, and a basic Capacity Planner.",
  },
  {
    title: "Manufacturing Plan Hub",
    date: "June 2026",
    text: "Manufacturing Plans now connect calculations, setup sheets, travelers, inspection records, matched tools, SOPs, QR links, Shop Brain notes, and saved AI conversations.",
  },
  {
    title: "Connected Calculator Results",
    date: "June 2026",
    text: "Feeds & Speeds and Bolt Pattern results can be reused in G-code workflows, with structured result lineage and unit-aware imports.",
  },
  {
    title: "Spring Steel Material Expansion",
    date: "June 2026",
    text: "Added common carbon, alloy, stainless, and wire spring materials including 1074, 1075, 1080, 1095, 5160, 6150, 9260, 17-7 PH, 301, 302, 304 spring temper, and ASTM A228 music wire.",
  },
  {
    title: "Advanced Mode Improvements",
    date: "June 2026",
    text: "Advanced Mode now changes real calculations and generated output with machine limits, cutting-data overrides, rigidity, stickout, engagement, coolant, G-code post settings, bolt-pattern transforms, and decision-support traces.",
  },
  {
    title: "Bolt Pattern Accuracy Upgrade",
    date: "June 2026",
    text: "Bolt Pattern previews now scale actual hole and slot geometry, preserve coordinates and aspect ratio, identify overlapping holes, and carry overlap warnings into tables and exports.",
  },
];

const pageMeta = {
  formulas: {
    kicker: "Reference",
    title: "Formula Reference",
    subtitle: "A practical reference for the shop math used throughout ManuCalc.",
  },
  materials: {
    kicker: "Data",
    title: "Material Data",
    subtitle: "Shared material presets, machining baselines, hardness references, tool materials, and coatings.",
  },
  resources: {
    kicker: "Resources",
    title: "Manufacturing Resources",
    subtitle: "Practical guidance for using ManuCalc safely in quoting, machining, inspection, and production planning.",
  },
  whatsNew: {
    kicker: "Product",
    title: "What's New",
    subtitle: "Recent ManuCalc improvements and platform updates.",
  },
  about: {
    kicker: "About",
    title: "About ManuCalc",
    subtitle: "ManuCalc is being built as a one-stop manufacturing calculation and planning platform.",
  },
};

const ReferenceHero = ({ meta }) => (
  <section className="reference-hero">
    <span className="reference-kicker">{meta.kicker}</span>
    <h1>{meta.title}</h1>
    <p>{meta.subtitle}</p>
  </section>
);

const FormulaReference = () => {
  const location = useLocation();
  const initialSearch = new URLSearchParams(location.search).get("search") || "";
  const [auditQuery, setAuditQuery] = useState("");
  const [accuracyFilter, setAccuracyFilter] = useState("all");
  const [formulaQuery, setFormulaQuery] = useState(initialSearch);
  const [formulaCategory, setFormulaCategory] = useState("all");
  const filteredFormulas = useMemo(() => {
    const normalized = formulaQuery.trim().toLowerCase();
    return FORMULA_REFERENCE_DATABASE
      .filter((item) => formulaCategory === "all" || item.category === formulaCategory)
      .filter((item) =>
        !normalized ||
        [
          item.name,
          item.category,
          item.formula,
          item.units,
          item.example,
          item.notes,
          item.source,
          item.sourceEdition,
          item.lastVerified,
          ...(item.assumptions || []),
          ...Object.keys(item.variables || {}),
          ...Object.values(item.variables || {}),
          ...(item.relatedCalculators || []),
          ...(item.tags || []),
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalized)
      );
  }, [formulaCategory, formulaQuery]);
  const filteredAudit = useMemo(() => {
    const normalized = auditQuery.trim().toLowerCase();
    return STANDARDS_AUDIT
      .map((item) => ({
        ...item,
        calculator: CALCULATOR_TOOLS.find((tool) => tool.id === item.id),
      }))
      .filter((item) => accuracyFilter === "all" || item.level === accuracyFilter)
      .filter((item) =>
        !normalized ||
        [
          item.calculator?.name,
          item.level,
          item.basis,
          item.verify,
          ...(item.notes || []),
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalized)
      );
  }, [accuracyFilter, auditQuery]);
  const accuracyLevels = Array.from(new Set(STANDARDS_AUDIT.map((item) => item.level)));

  return (
    <>
      <ReferenceHero meta={pageMeta.formulas} />
      <section className="reference-card accent-cyan">
        <span className="reference-card-badge">Searchable Formula Database</span>
        <p className="reference-note">
          Search by formula name, variable, process, calculator, unit, symbol, or shop keyword.
        </p>
        <div className="reference-controls">
          <label>
            Search formulas
            <input
              type="search"
              value={formulaQuery}
              onChange={(event) => setFormulaQuery(event.target.value)}
              placeholder="tap drill, SFM, bolt circle, true position, heat input..."
            />
          </label>
          <label>
            Formula category
            <select value={formulaCategory} onChange={(event) => setFormulaCategory(event.target.value)}>
              <option value="all">All formula categories</option>
              {FORMULA_CATEGORIES.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="formula-reference-list">
          {filteredFormulas.map((item) => (
            <article key={`${item.category}-${item.name}`} className="formula-reference-row">
              <div>
                <span>{item.category}</span>
                <strong>{item.name}</strong>
              </div>
              <code>{item.formula}</code>
              <dl>
                {Object.entries(item.variables || {}).map(([symbol, definition]) => (
                  <React.Fragment key={symbol}>
                    <dt>{symbol}</dt>
                    <dd>{definition}</dd>
                  </React.Fragment>
                ))}
              </dl>
              <p><b>Units:</b> {item.units || "See formula context."}</p>
              <p><b>Example:</b> {item.example}</p>
              <p><b>Source:</b> {item.source || "Industry standard formula"}</p>
              <p><b>Source edition:</b> {item.sourceEdition || "Not edition-specific"}</p>
              {item.lastVerified && <p><b>Last verified:</b> {item.lastVerified}</p>}
              {item.assumptions?.length > 0 && (
                <p><b>Assumptions:</b> {item.assumptions.join("; ")}</p>
              )}
              {item.notes && <p><b>Note:</b> {item.notes}</p>}
              {item.relatedCalculators?.length > 0 && (
                <div className="related-formula-links">
                  {item.relatedCalculators.map((calculatorId) => (
                    <Link key={calculatorId} to={`/calculator/${calculatorId}`}>{calculatorId}</Link>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      </section>

      <section className="reference-card accent-purple">
        <span className="reference-card-badge">Standards Audit</span>
        <p className="reference-note">
          ManuCalc is being maintained as a Handbook-style searchable reference: formulas are separated from estimates, and each tool lists what must be verified before production use.
        </p>
        <div className="reference-controls">
          <label>
            Search audit
            <input
              type="search"
              value={auditQuery}
              onChange={(event) => setAuditQuery(event.target.value)}
              placeholder="NPT, GD&T, horsepower, press brake..."
            />
          </label>
          <label>
            Accuracy level
            <select value={accuracyFilter} onChange={(event) => setAccuracyFilter(event.target.value)}>
              <option value="all">All accuracy levels</option>
              {accuracyLevels.map((level) => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="audit-list">
          {filteredAudit.map((item) => (
            <article key={item.id} className="audit-row">
              <div>
                <strong>{item.calculator?.name || item.id}</strong>
                <span>{item.level}</span>
              </div>
              <p>{item.basis}</p>
              <p><b>Verify:</b> {item.verify}</p>
            </article>
          ))}
        </div>
      </section>

      <div className="reference-grid two-column">
        {formulaSections.map((section) => (
          <section key={section.title} className={`reference-card accent-${section.accent}`}>
            <span className="reference-card-badge">{section.title}</span>
            <div className="formula-list">
              {section.formulas.map(([name, formula, note]) => (
                <article key={name} className="formula-row">
                  <strong>{name}</strong>
                  <code>{formula}</code>
                  <p>{note}</p>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  );
};

const MaterialData = () => {
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("all");
  const materials = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return Object.entries(MATERIAL_DATABASE)
      .map(([id, material]) => ({ id, ...material }))
      .filter((material) => group === "all" || material.group === group)
      .filter((material) =>
        !normalized ||
        [
          material.label,
          material.group,
          getRockwellHardness(material.hardnessHB),
          String(material.sfm),
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalized)
      );
  }, [group, query]);

  return (
    <>
      <ReferenceHero meta={pageMeta.materials} />
      <section className="reference-card reference-controls-card accent-cyan">
        <div className="reference-controls">
          <label>
            Search material
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Hardox, 4140, 6061, stainless..."
            />
          </label>
          <label>
            Material group
            <select value={group} onChange={(event) => setGroup(event.target.value)}>
              <option value="all">All material groups</option>
              {Object.entries(MATERIAL_GROUPS).map(([id, label]) => (
                <option key={id} value={id}>{label}</option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="reference-card">
        <span className="reference-card-badge">Machining Material Presets</span>
        <div className="reference-table-wrap">
          <table className="reference-table">
            <thead>
              <tr>
                <th>Material</th>
                <th>Group</th>
                <th>SFM Baseline</th>
                <th>Chip Load</th>
                <th>Approx. Rockwell</th>
                <th>Machinability</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((material) => (
                <tr key={material.id}>
                  <td>{material.label}</td>
                  <td>{MATERIAL_GROUPS[material.group] || material.group}</td>
                  <td>{material.sfm}</td>
                  <td>{material.chipLoad}</td>
                  <td>{getRockwellHardness(material.hardnessHB)}</td>
                  <td>{material.machinability}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="reference-note">
          These values are practical baselines, not guaranteed cutting data. {HARDNESS_DATA_POLICY.conversionNote}
        </p>
      </section>

      <section className="reference-card accent-orange">
        <span className="reference-card-badge">Rockwell Hardness Audit Policy</span>
        <div className="formula-list">
          <article className="formula-row">
            <strong>Standards basis</strong>
            <code>{HARDNESS_DATA_POLICY.testStandard}</code>
            <p>Rockwell B and C are different test scales. The displayed scale changes with the approximate hardness range; it is not a direct material specification.</p>
          </article>
          <article className="formula-row">
            <strong>Condition required</strong>
            <p>{HARDNESS_DATA_POLICY.conditionNote}</p>
          </article>
          <article className="formula-row">
            <strong>Acceptance rule</strong>
            <p>Use the drawing, material specification, heat-treatment requirement, supplier certification, or a valid hardness test on the actual part. Do not accept or reject material from a converted database estimate.</p>
          </article>
        </div>
      </section>

      <div className="reference-grid two-column">
        <section className="reference-card accent-purple">
          <span className="reference-card-badge">Tool Materials</span>
          <div className="mini-data-grid">
            {Object.entries(TOOL_MATERIAL_OPTIONS).map(([id, tool]) => (
              <div key={id}>
                <strong>{tool.label}</strong>
                <span>Base life: {tool.baseLifeMinutes} min</span>
                <span>Wear factor: {tool.wearResistance}</span>
              </div>
            ))}
          </div>
        </section>
        <section className="reference-card accent-green">
          <span className="reference-card-badge">Tool Coatings</span>
          <div className="mini-data-grid">
            {Object.entries(TOOL_COATINGS).map(([id, coating]) => (
              <div key={id}>
                <strong>{coating.label}</strong>
                <span>SFM x {coating.sfmMultiplier}</span>
                <span>Wear x {coating.wearResistanceMultiplier}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

const ResourcesPage = () => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const categories = Array.from(new Set(resourceCards.map((resource) => resource.category)));
  const filteredResources = resourceCards.filter((resource) => {
    const normalized = query.trim().toLowerCase();
    const matchesCategory = category === "all" || resource.category === category;
    const matchesQuery =
      !normalized ||
      [
        resource.title,
        resource.category,
        resource.description,
        ...(resource.tags || []),
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalized);
    return matchesCategory && matchesQuery;
  });

  return (
    <>
      <ReferenceHero meta={pageMeta.resources} />
      <section className="reference-card reference-controls-card accent-cyan">
        <div className="reference-controls">
          <label>
            Search resources
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="threads, GD&T, welding, bend allowance, quoting..."
            />
          </label>
          <label>
            Resource category
            <select value={category} onChange={(event) => setCategory(event.target.value)}>
              <option value="all">All resource categories</option>
              {categories.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </label>
        </div>
      </section>
      <div className="resource-card-grid">
        {filteredResources.map((resource) => (
          <Link key={resource.title} to={resource.link} className="resource-library-card">
            <span>{resource.category}</span>
            <strong>{resource.title}</strong>
            <p>{resource.description}</p>
            <div>
              {resource.tags.map((tag) => (
                <small key={tag}>{tag}</small>
              ))}
            </div>
          </Link>
        ))}
      </div>
      <div className="reference-grid two-column">
        {resources.map((section) => (
          <section key={section.title} className={`reference-card accent-${section.accent}`}>
            <span className="reference-card-badge">{section.title}</span>
            <ul className="reference-list">
              {section.items.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </section>
        ))}
      </div>
    </>
  );
};

const WhatsNewPage = () => (
  <>
    <ReferenceHero meta={pageMeta.whatsNew} />
    <section className="reference-card">
      <div className="timeline-list">
        {whatsNew.map((item) => (
          <article key={item.title} className="timeline-item">
            <span>{item.date}</span>
            <div>
              <strong>{item.title}</strong>
              <p>{item.text}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  </>
);

const AboutPage = () => (
  <>
    <section className="reference-hero about-product-hero">
      <span className="reference-kicker">About ManuCalc</span>
      <div className="about-hero-brand">
        <span>MC</span>
        <strong>ManuCalc</strong>
      </div>
      <h1>The Manufacturing Toolkit Built for Real Shops</h1>
      <p>
        ManuCalc combines shop calculators, material data, manufacturing plans,
        SOP workflows, 3S improvement tracking, and reference content into one
        professional workspace for modern manufacturing.
      </p>
      <div className="about-hero-actions">
        <Link to="/dashboard" className="premium-primary-link">Open Calculators</Link>
        <Link to="/jobs" className="premium-secondary-link">Create a Manufacturing Plan</Link>
      </div>
    </section>

    <section className="reference-card accent-purple about-mission-card">
      <span className="reference-card-badge">Our Mission</span>
      <h2>The online Machinery Handbook workflow for the way shops actually work.</h2>
      <p>
        ManuCalc exists because manufacturing decisions rarely live in one neat
        place. A machinist needs a tap drill, an estimator needs cycle and
        material logic, a supervisor needs a process plan, and a shop owner
        needs the work tied back to a customer/job. ManuCalc is built to connect
        those decisions instead of leaving them scattered across notebooks,
        spreadsheets, screenshots, and memory.
      </p>
    </section>

    <section className="reference-card accent-cyan">
      <span className="reference-card-badge">Who Uses ManuCalc</span>
      <div className="about-persona-grid">
        {[
          ["Machinists", "Fast shop math, holemaking, threading, feeds, speeds, and setup support."],
          ["CNC Programmers", "G-code starters, operation context, tooling data, and manufacturing-plan links."],
          ["Engineers", "GD&T help, tolerance checks, material data, and design-for-manufacturing review."],
          ["Welders", "Heat input, weld cost estimates, process notes, and fabrication calculators."],
          ["Fabricators", "Bends, kerf, press brake tonnage, material weight, and sheet yield tools."],
          ["Shop Owners", "Quotes, exports, employee permissions, plans, SOPs, and request-driven expansion."],
          ["Students", "Clear formulas, practical notes, and searchable reference material."],
        ].map(([title, text]) => (
          <article key={title}>
            <strong>{title}</strong>
            <p>{text}</p>
          </article>
        ))}
      </div>
    </section>

    <section className="reference-card accent-green">
      <span className="reference-card-badge">Platform Features</span>
      <div className="about-feature-grid">
        {[
          ["Calculators", `${CALCULATOR_TOOLS.length} tools across machining, welding, fabrication, inspection, quoting, materials, and general shop math.`],
          ["Manufacturing Plans", "Save calculations under the job, customer, and part they were made for."],
          ["SOP Builder", "Build machine-by-machine process steps with tooling, inspection, time, cost, and notes."],
          ["Resources Database", "Search formulas, standards notes, practical warnings, and manufacturing references."],
          ["Material Database", "Shared material presets with machining baselines, Hardox grades, steels, aluminum, stainless, plastics, and superalloys."],
          ["3S Workspace", "Track Sort, Set In Order, and Shine projects with photos, stages, owners, and due dates."],
          ["Job Planner", "Plan machine routes, files, costs, outputs, risks, and process details in one place."],
          ["Custom Requests", "Users can submit calculator/tool requests directly to the ManuCalc platform owner."],
        ].map(([title, text]) => (
          <article key={title}>
            <span>{title.slice(0, 2).toUpperCase()}</span>
            <strong>{title}</strong>
            <p>{text}</p>
          </article>
        ))}
      </div>
    </section>

    <div className="reference-grid two-column">
      <section className="reference-card accent-yellow">
        <span className="reference-card-badge">Why It Is Different</span>
        <ul className="reference-list">
          <li>Built around real manufacturing workflows, not generic engineering forms.</li>
          <li>Connects calculator results to jobs, customers, parts, plans, and SOPs.</li>
          <li>Separates formula-based outputs from estimates and flags what must be verified.</li>
          <li>Supports lean workflow thinking, 3S project tracking, and practical shop-floor use.</li>
          <li>Keeps business permissions separate from platform-level product changes.</li>
        </ul>
      </section>

      <section className="reference-card accent-orange">
        <span className="reference-card-badge">Roadmap</span>
        <div className="timeline-list compact">
          {[
            ["Desktop App", "Signed Tauri desktop builds for Windows, macOS, and Linux."],
            ["Mobile App", "Capacitor Android/iOS packaging for installed shop-floor use."],
            ["AI Planning", "Assisted process planning, risk review, and quote support."],
            ["More Calculators", "Deeper Handbook-style coverage across manufacturing fields."],
            ["ERP Integrations", "Future connections to quoting, inventory, and production systems."],
          ].map(([title, text]) => (
            <article key={title} className="timeline-item">
              <span>{title}</span>
              <div>
                <strong>{title}</strong>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>

    <section className="reference-card accent-cyan">
      <span className="reference-card-badge">What ManuCalc Covers</span>
      <div className="category-chip-grid">
        {CALCULATOR_CATEGORIES.map((category) => (
          <Link key={category.id} to={`/dashboard?category=${category.id}`}>
            <span>{category.icon}</span>
            {category.name}
          </Link>
        ))}
      </div>
    </section>

    <section className="reference-card about-cta-card">
      <span className="reference-card-badge">Start Building</span>
      <h2>Use ManuCalc as the working layer between the print, the machine, and the job.</h2>
      <div className="about-hero-actions">
        <Link to="/dashboard" className="premium-primary-link">Browse Calculators</Link>
        <Link to="/process-builder" className="premium-secondary-link">Open SOP Builder</Link>
        <Link to="/resources" className="premium-secondary-link">View Resources</Link>
      </div>
    </section>
  </>
);

const ReferencePage = ({ type }) => {
  const activeType = type || "resources";
  const pages = {
    formulas: <FormulaReference />,
    materials: <MaterialData />,
    resources: <ResourcesPage />,
    whatsNew: <WhatsNewPage />,
    about: <AboutPage />,
  };

  return (
    <div className="reference-page">
      {pages[activeType] || pages.resources}
    </div>
  );
};

export default ReferencePage;
